import { FC } from 'react';
import {
  Modal,
  Alert,
  Switch,
  Typography,
  Skeleton,
} from 'antd';
import FlexBox from '../../../../components/atoms/FlexBox';
import FlexCol from '../../../../components/atoms/FlexCol';
import SignifyDistributorSelector from './components/SignifyDistributorSelector';
import SignifyProductSearch from './components/SignifyProductSearch';
import SignifyProductDetailsWithMapping from './components/SignifyProductDetailsWithMapping';
import SignifyModalActionButtons from './components/SignifyModalActionButtons';
import { SignifyProductModalProps } from '../index.types';

const { Text } = Typography;

/**
 * Modal component for searching and selecting Signify products
 * Provides distributor selection, product search, field mapping, and insert functionality
 */
const SignifyProductModal: FC<SignifyProductModalProps> = ({
  open,
  onCancel,
  selectedDistributor,
  selectedProduct,
  productSearchValue,
  distributorSearchValue,
  filteredDistributors,
  filteredDistributorsCount,
  totalDistributors,
  distributorSearchTerm,
  loadingDistributors,
  productOptions,
  searchingProducts,
  productPriceAndAvailabilityData,
  loadingPrice,
  sourceMappings,
  sourceRules,
  constantMappings,
  customMappings,
  availableTableColumns,
  duplicateMappings,
  validateSingleMapping,
  sourceTypesMap,
  destinationFieldsMap,
  hasValidationErrors,
  recordNameInfo,
  destinationEntityLabel,
  error,
  onProductSearch,
  onProductSelect,
  onProductClear,
  onDistributorSearch,
  onDistributorSelect,
  onDistributorClear,
  onMappingChange,
  onInsertIntoTable,
  onRefreshDistributors,
  getColumnDisplayName,
  shouldShowFooter,
  saveRecord,
  onSaveRecordChange,
}) => {
  return (
    <Modal
      title={
        <FlexBox gap={0} alignStart justifyStart>
          <span>Insert From Signify API</span>
        </FlexBox>
      }
      open={open}
      onCancel={onCancel}
      width={800}
      footer={
        <SignifyModalActionButtons
          onCancel={onCancel}
          onInsert={onInsertIntoTable}
          loadingPrice={loadingPrice}
          productPriceAndAvailabilityData={productPriceAndAvailabilityData}
          hasValidationErrors={hasValidationErrors}
          shouldShowFooter={shouldShowFooter}
        />
      }
    >
      <FlexCol gap={10}>
        <SignifyDistributorSelector
          selectedDistributor={selectedDistributor}
          distributorSearchValue={distributorSearchValue}
          loading={loadingPrice}
          loadingDistributors={loadingDistributors}
          filteredDistributors={filteredDistributors}
          filteredDistributorsCount={filteredDistributorsCount}
          totalDistributors={totalDistributors}
          distributorSearchTerm={distributorSearchTerm}
          onDistributorSearch={onDistributorSearch}
          onDistributorSelect={onDistributorSelect}
          onDistributorClear={onDistributorClear}
          onRefreshDistributors={onRefreshDistributors}
        />
        
        <SignifyProductSearch
          productSearchValue={productSearchValue}
          productOptions={productOptions}
          searchingProducts={searchingProducts}
          loading={loadingPrice}
          onProductSearch={onProductSearch}
          onProductSelect={onProductSelect}
          onProductClear={onProductClear}
        />

        {selectedProduct && !loadingPrice && (
          <>
            <FlexBox justifyStart gap={5}>
              <Switch
                size="small"
                checked={saveRecord}
                onChange={onSaveRecordChange}
                aria-label="signify-save-record-toggle"
              />
              <Text type="secondary">Also save as record</Text>
            </FlexBox>

            <SignifyModalActionButtons
              onCancel={onCancel}
              onInsert={onInsertIntoTable}
              loadingPrice={loadingPrice}
              productPriceAndAvailabilityData={productPriceAndAvailabilityData}
              hasValidationErrors={hasValidationErrors}
              shouldShowFooter={shouldShowFooter}
            />

            <SignifyProductDetailsWithMapping
              selectedProduct={selectedProduct}
              productPriceAndAvailabilityData={productPriceAndAvailabilityData}
              sourceMappings={sourceMappings}
              sourceRules={sourceRules}
              constantMappings={constantMappings}
              customMappings={customMappings}
              availableTableColumns={availableTableColumns}
              duplicateMappings={duplicateMappings}
              validateSingleMapping={validateSingleMapping}
              sourceTypesMap={sourceTypesMap}
              destinationFieldsMap={destinationFieldsMap}
              recordNameInfo={recordNameInfo}
              destinationEntityLabel={destinationEntityLabel}
              getColumnDisplayName={getColumnDisplayName}
              onMappingChange={onMappingChange}
            />
          </>
        )}

        {loadingPrice && (
          <FlexCol justifyStart gap={10} style={{ width: '100%' }}>
            <Text type='secondary'>Fetching product, price, and availability from Signify API...</Text>
            <Skeleton active />
          </FlexCol>
        )}

        {error && (
          <Alert
            title="Error"
            description={error}
            type="error"
            showIcon={false}
            style={{ width: '100%' }}
          />
        )}
      </FlexCol>
    </Modal>
  );
};

export default SignifyProductModal;